import { dedupeProfessionals } from '@/lib/dedupeProfessionals';

interface ApiProfessional {
  ID_PRESTADOR: string;
  DOMICONS_ID: number;
  ORDEN_PRESTADOR: number;
  NOMBRE_PRESTADOR: string;
  PRESTORDER: string;
  CARTILLA: string;
  RUBRO: string;
  ESPECIALIDAD: string;
  CALLE: string;
  NUMERO: number;
  PISO?: number | string;
  DPTO?: string;
  LOCALIDAD: string;
  BARRIO: string;
  ZONA: string;
  TELEFONO: string;
  REFERENCIAS: string;
  LOCATION_LAT: number;
  LOCATION_LONG: number;
}

export interface Professional {
  CODIGO: string;
  NOMBRE: string;
  APELLIDO: string;
  MATRICULA: string;
  ESPECIALIDAD: string;
  DIRECCION: string;
  TELEFONO: string;
  EMAIL?: string;
  HORARIOS?: string;
  OBSERVACIONES?: string;
  LOCATION_LAT: number;
  LOCATION_LONG: number;
  CALLE: string;
  NUMERO: number;
  LOCALIDAD: string;
  BARRIO: string;
  ZONA: string;
  RUBRO?: string; // Tipo de prestador (centro propio / sanatorio / profesional) — analytics
  globalIndex?: number; // Índice global para paginación
  distanceKm?: number; // Distancia real al centro de búsqueda (orden por cercanía)
  distanceEstimatedKm?: number; // Distancia estimada vía centro de localidad (prestadores sin coords)
}

export interface SearchResults {
  professionals: Professional[];
  totalResults: number;
  totalPages: number;
  currentPage: number;
  itemsPerPage: number;
}

export interface SearchFetchParams {
  plan: string;
  searchTypeCode: string;
  especialidadNombre?: string;
  vTexto?: string;
  locationData?: {
    TIPOLOC: string;
    ZONA: string;
    USERLOCATION: string;
    PROVINCIA: string;
    SYSTEMLOCATION: string;
  };
}

const EMPTY_RESULTS: SearchResults = {
  professionals: [],
  totalResults: 0,
  totalPages: 0,
  currentPage: 1,
  itemsPerPage: 16,
};

function trimLocationValue(value: string | undefined): string {
  const trimmed = value?.trim() || '';
  return trimmed || '=';
}

/** Códigos del backend (Z1, FORMOSA). Descarta textos de display con espacios. */
function normalizeBackendCode(value: string | undefined): string {
  const trimmed = trimLocationValue(value);
  if (trimmed === '=') return '=';
  if (trimmed.includes(' ')) return '=';
  return trimmed;
}

export function buildSearchQueryParams({
  plan,
  searchTypeCode,
  especialidadNombre,
  vTexto,
  locationData,
}: SearchFetchParams): Record<string, string> {
  const baseParams = {
    plan,
    searchTypeCode,
    especialidadNombre: especialidadNombre?.trim() || '=',
    vTexto: vTexto?.trim() || '=',
    searchText: '=',
  };

  if (!locationData) {
    return {
      ...baseParams,
      zona: '=',
      provincia: '=',
      localidad: '=',
      barrio: '=',
    };
  }

  const tipoloc = locationData.TIPOLOC?.trim().toUpperCase() || '';
  const zona = normalizeBackendCode(locationData.ZONA);
  const provincia = normalizeBackendCode(locationData.PROVINCIA);
  const systemLocation = normalizeBackendCode(locationData.SYSTEMLOCATION);

  switch (tipoloc) {
    case 'ZONA':
      return {
        ...baseParams,
        zona,
        provincia: '=',
        localidad: '=',
        barrio: '=',
      };

    case 'PROVINCIA':
      // En un pick explícito de provincia el valor ES el nombre real (p. ej.
      // "BUENOS AIRES"), que el backend acepta con espacios. Por eso usamos el
      // valor literal y NO normalizeBackendCode (que descartaría todo lo que
      // tenga espacios, dejando provincia="=" → consulta a todo el país).
      return {
        ...baseParams,
        zona: '=',
        provincia: trimLocationValue(locationData.PROVINCIA),
        localidad: '=',
        barrio: '=',
      };

    case 'LOCALIDAD':
    case 'BARRIO':
      // VEN-419: no filtramos por localidad/barrio exacto — el backend lo
      // trata como match estricto y da muy pocos o 0 resultados si esa
      // localidad puntual no tiene prestadores de una especialidad+plan (ej.
      // "Firmat" da 1 resultado propio cuando hay 43 a pocos km, en Venado
      // Tuerto). Pedimos toda la zona y dejamos que sortByDistanceHybrid
      // (lib/geo.ts) ordene por cercanía a la localidad/barrio elegido en
      // medical-directory.tsx. Empezó siendo solo para barrios de CABA, pero
      // el mismo problema aplica a cualquier localidad chica del país.
      return {
        ...baseParams,
        zona,
        provincia,
        localidad: '=',
        barrio: '=',
      };

    default:
      if (zona !== '=' && (provincia === '=' || systemLocation === zona)) {
        return {
          ...baseParams,
          zona,
          provincia: '=',
          localidad: '=',
          barrio: '=',
        };
      }

      if (provincia !== '=' && zona === '=') {
        return {
          ...baseParams,
          zona: '=',
          provincia,
          localidad: '=',
          barrio: '=',
        };
      }

      if (zona !== '=' && provincia !== '=') {
        return {
          ...baseParams,
          zona,
          provincia,
          localidad: systemLocation !== '=' ? systemLocation : '=',
          barrio: '=',
        };
      }

      return {
        ...baseParams,
        zona: '=',
        provincia: '=',
        localidad: '=',
        barrio: '=',
      };
  }
}

function normalizeDisplayText(text: string): string {
  if (!text || text.trim() === '') return '';

  return text
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function parseProfessionalsResponse(data: { PROFESIONAL?: ApiProfessional | ApiProfessional[] }): ApiProfessional[] {
  const raw = data.PROFESIONAL;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  return [raw];
}

export async function fetchSearchResults(params: SearchFetchParams): Promise<SearchResults> {
  if (!params.plan || !params.searchTypeCode) {
    return EMPTY_RESULTS;
  }

  const queryParams = new URLSearchParams(buildSearchQueryParams(params));
  const res = await fetch(`/api/search?${queryParams.toString()}`);

  if (!res.ok) {
    throw new Error('Error al buscar médicos');
  }

  const data = await res.json();
  const professionalsArray = parseProfessionalsResponse(data);

  if (professionalsArray.length === 0) {
    return EMPTY_RESULTS;
  }

  const mappedProfessionals = professionalsArray.map((professional: ApiProfessional): Professional => {
    const nameParts = professional.NOMBRE_PRESTADOR.split(',');
    const apellido = normalizeDisplayText(nameParts[0]?.trim() || '');
    const nombre = normalizeDisplayText(nameParts[1]?.trim() || professional.NOMBRE_PRESTADOR);

    return {
      CODIGO: professional.ID_PRESTADOR,
      NOMBRE: nombre,
      APELLIDO: apellido,
      MATRICULA: professional.DOMICONS_ID.toString(),
      ESPECIALIDAD: professional.ESPECIALIDAD,
      DIRECCION: normalizeDisplayText(
        `${professional.CALLE && professional.CALLE !== 'undefined' ? professional.CALLE : ''}${professional.NUMERO ? ` ${professional.NUMERO}` : ''}${professional.PISO ? ` piso ${professional.PISO}` : ''}${professional.DPTO ? ` dpto ${professional.DPTO}` : ''}`
      ),
      TELEFONO: professional.TELEFONO != null ? String(professional.TELEFONO) : '',
      EMAIL: '',
      HORARIOS: '',
      OBSERVACIONES: professional.REFERENCIAS,
      LOCATION_LAT: professional.LOCATION_LAT,
      LOCATION_LONG: professional.LOCATION_LONG,
      CALLE: professional.CALLE && professional.CALLE !== 'undefined' ? professional.CALLE : '',
      NUMERO: professional.NUMERO,
      LOCALIDAD: normalizeDisplayText(professional.LOCALIDAD),
      BARRIO: professional.BARRIO,
      ZONA: professional.ZONA,
      RUBRO: professional.RUBRO ?? '',
    };
  });

  const dedupedProfessionals = dedupeProfessionals(mappedProfessionals);
  const itemsPerPage = 16;

  return {
    professionals: dedupedProfessionals,
    totalResults: dedupedProfessionals.length,
    totalPages: Math.ceil(dedupedProfessionals.length / itemsPerPage),
    currentPage: 1,
    itemsPerPage,
  };
}
